import {
  Cancel,
  Delete,
  DragIndicator,
  Edit,
  InfoOutlined,
  Search,
} from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useNavigation } from "contexts/notificationsContext";
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useAuth } from "shared/hooks/auth";
import {
  alterarStatusGatilho,
  excluirGatilho,
  excluirSubtarefa,
  getConfiguracaoTarefasEmpresa,

  getReferenciasParaSelecao,
  getTiposAtividade,
  IEmpresaTarefaGatilho,
  IEmpresaTarefaReferencia,

  IReferenciaOption,
  ISalvarGatilhoRequest,
  ISalvarSubtarefaRequest,
  ITipoAtividade,
  salvarGatilho,
  salvarReferenciasEmpresa,
} from "services/modules/tarefas";
import ConfirmDeleteModal from "components/ConfirmDeleteModal";

// ============================================
// COMPONENTE AUXILIAR: InfoTooltip
// ============================================

const InfoTooltip = ({ text }: { text: string }) => (
  <Tooltip
    title={text}
    arrow
    componentsProps={{
      tooltip: {
        sx: {
          backgroundColor: "#D4EDDA",
          color: "#155724",
          maxWidth: 300,
          p: "12px 16px",
          borderRadius: 1,
          fontSize: 12,
        },
      },
      arrow: { sx: { color: "#D4EDDA" } },
    }}
  >
    <InfoOutlined sx={{ fontSize: 16, color: "#5CB85C", ml: 1, cursor: "help" }} />
  </Tooltip>
);

interface TarefasFormProps {
  onCancel?: () => void;
}

// Interface para props do componente de linha sortable
interface SortableSubtarefaRowProps {
  id: string;
  index: number;
  subtarefa: ISalvarSubtarefaRequest;
  modoVisualizacao: boolean;
  onUpdate: (index: number, campo: keyof ISalvarSubtarefaRequest, valor: any) => void;
  onRemove: (index: number) => void;
  onRequestDelete: (index: number) => void;
}

// Componente de linha sortable para sub-tarefas
const SortableSubtarefaRow = ({
  id,
  index,
  subtarefa,
  modoVisualizacao,
  onUpdate,
  onRequestDelete,
}: SortableSubtarefaRowProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    backgroundColor: isDragging ? "#f5f5f5" : "inherit",
  };

  return (
    <TableRow ref={setNodeRef} style={style}>
      {/* Drag */}
      <TableCell width={40}>
        {!modoVisualizacao && (
          <Box
            sx={{ cursor: "grab", display: "flex", alignItems: "center", gap: 0.5 }}
            {...attributes}
            {...listeners}
          >
            <DragIndicator color="primary" />
            <InfoTooltip text="Recurso para permitir a reordenação das subtarefas pelo usuário." />
          </Box>
        )}
      </TableCell>
      {/* Número badge azul */}
      <TableCell width={40}>
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: "50%",
            backgroundColor: "#3C90AD",
            color: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 13,
          }}
        >
          {index + 1}
        </Box>
      </TableCell>
      {/* Nome da sub-tarefa (a coluna sempre gravou o nome; o rótulo "Etapa" era enganoso — FASE 04 item 3) */}
      <TableCell>
        <TextField
          size="small"
          fullWidth
          value={subtarefa.nome}
          onChange={(e) => onUpdate(index, "nome", e.target.value)}
          placeholder="Informe o nome da sub-tarefa"
          disabled={modoVisualizacao}
        />
      </TableCell>
      {/* Switch */}
      <TableCell align="center" width={60}>
        <Switch
          size="small"
          checked={subtarefa.ativo}
          onChange={(e) => onUpdate(index, "ativo", e.target.checked)}
          disabled={modoVisualizacao}
        />
      </TableCell>
      {/* Excluir */}
      {!modoVisualizacao && (
        <TableCell width={50}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <IconButton
              size="small"
              color="error"
              onClick={() => onRequestDelete(index)}
              title="Excluir"
              sx={{
                "&:hover": { backgroundColor: "#F0F0F0" },
                "&:active": { backgroundColor: "#E0E0E0" },
              }}
            >
              <Cancel fontSize="small" />
            </IconButton>
            <InfoTooltip text="A remoção de uma subtarefa somente será possível caso ela ainda não tenha sido utilizada numa Tarefa. A qualquer momento será possível desativar uma subtarefa para que nos próximos gatilhos ela seja desconsiderada." />
          </Box>
        </TableCell>
      )}
    </TableRow>
  );
};

/**
 * Componente da aba de Tarefas no Cadastro de Empresas
 * Fase 4 - Etapa 1: Configuração de Tarefas
 */
export const TarefasForm = ({ onCancel }: TarefasFormProps) => {
  const params = useParams();
  const idEmpresa = Number(params.id);
  const { openNotification } = useNavigation();
  const { havePermission } = useAuth();

  // Gestão de gatilhos/tipos de atividade restrita a perfis de administração
  // (mesmas roles que liberam o cadastro de empresas/gerência)
  const temPermissaoGerenciarTarefas = havePermission([
    "Master",
    "Configuracao",
    "GerenciadorTarefas",
  ]);

  // Estados de carregamento
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Dados de referência
  const [referenciasOptions, setReferenciasOptions] = useState<IReferenciaOption[]>([]);
  const [tiposAtividade, setTiposAtividade] = useState<ITipoAtividade[]>([]);


  // Dados da empresa
  const [referenciasEmpresa, setReferenciasEmpresa] = useState<IEmpresaTarefaReferencia[]>([]);
  const [referenciaSelecionadas, setReferenciasSelecionadas] = useState<number[]>([]);
  const [gatilhos, setGatilhos] = useState<IEmpresaTarefaGatilho[]>([]);

  // Modal de edição de gatilho
  const [modalGatilhoOpen, setModalGatilhoOpen] = useState(false);
  const [gatilhoEditando, setGatilhoEditando] = useState<IEmpresaTarefaGatilho | null>(null);
  const [modoVisualizacao, setModoVisualizacao] = useState(false);
  const [formGatilho, setFormGatilho] = useState<{
    idTipoAtividade: number | null;
    ativo: boolean;
    subtarefas: ISalvarSubtarefaRequest[];
  }>({
    idTipoAtividade: null,
    ativo: true,
    subtarefas: [],
  });

  // Estado original do formulário para detecção de mudanças
  const [formGatilhoOriginal, setFormGatilhoOriginal] = useState<string>("");

  // Modal de confirmação de descarte
  const [modalDescartarOpen, setModalDescartarOpen] = useState(false);

  // Modal de confirmação de exclusão de subtarefa
  const [modalExcluirSubtarefaOpen, setModalExcluirSubtarefaOpen] = useState(false);
  const [subtarefaExcluirIndex, setSubtarefaExcluirIndex] = useState<number | null>(null);

  // Modal de confirmação de exclusão de gatilho
  const [modalExcluirOpen, setModalExcluirOpen] = useState(false);
  const [gatilhoExcluir, setGatilhoExcluir] = useState<number | null>(null);

  // Paginação de sub-tarefas
  const [subtarefaPage, setSubtarefaPage] = useState(0);
  const [subtarefaRowsPerPage, setSubtarefaRowsPerPage] = useState(10);

  // Configuração do drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // ============================================
  // CARREGAMENTO DE DADOS
  // ============================================

  const carregarDadosIniciais = useCallback(async () => {
    if (!idEmpresa) return;

    setIsLoading(true);
    try {
      const [refs, tipos, config] = await Promise.all([
        getReferenciasParaSelecao(),
        getTiposAtividade(),
        getConfiguracaoTarefasEmpresa(idEmpresa),
      ]);

      setReferenciasOptions(refs);
      setTiposAtividade(tipos);


      if (config) {
        setReferenciasEmpresa(config.referencias);
        setReferenciasSelecionadas(config.referencias.map((r) => r.idReferencia));
        setGatilhos(config.gatilhos);
      }
    } catch (error) {
      openNotification({
        isOpen: true,
        message: "Erro ao carregar dados de tarefas",
        type: "error",
      });
    } finally {
      setIsLoading(false);
    }
  }, [idEmpresa, openNotification]);

  useEffect(() => {
    carregarDadosIniciais();
  }, [carregarDadosIniciais]);

  // ============================================
  // HANDLERS DE GATILHOS
  // ============================================

  const handleAbrirModalGatilho = (gatilho?: IEmpresaTarefaGatilho, readonly: boolean = false) => {
    setModoVisualizacao(readonly);
    if (gatilho) {
      setGatilhoEditando(gatilho);
      setFormGatilho({
        idTipoAtividade: gatilho.idTipoAtividade,
        ativo: gatilho.ativo,
        subtarefas: gatilho.subtarefas.map((s) => ({
          idEmpresaGatilhoSubtarefa: s.idEmpresaGatilhoSubtarefa,
          nome: s.nome,

          ordem: s.ordem,
          ativo: s.ativo,
        })),
      });
    } else {
      setGatilhoEditando(null);
      setFormGatilho({
        idTipoAtividade: null,
        ativo: true,
        subtarefas: [],
      });
    }
    setSubtarefaPage(0);
    setModalGatilhoOpen(true);

    const formInicial = gatilho
      ? {
          idTipoAtividade: gatilho.idTipoAtividade,
          ativo: gatilho.ativo,
          subtarefas: gatilho.subtarefas.map((s) => ({
            idEmpresaGatilhoSubtarefa: s.idEmpresaGatilhoSubtarefa,
            nome: s.nome,
  
            ordem: s.ordem,
            ativo: s.ativo,
          })),
        }
      : { idTipoAtividade: null, ativo: true, subtarefas: [] };
    setFormGatilhoOriginal(JSON.stringify(formInicial));
  };

  const handleFecharModalGatilho = () => {
    setModalGatilhoOpen(false);
    setGatilhoEditando(null);
    setModoVisualizacao(false);
    setFormGatilho({
      idTipoAtividade: null,
      ativo: true,
      subtarefas: [],
    });
    setFormGatilhoOriginal("");
    setSubtarefaPage(0);
  };

  const handleSalvarGatilho = async () => {
    // T-3.8 — Validação: atividade já configurada (duplicidade de gatilho)
    if (!gatilhoEditando && formGatilho.idTipoAtividade) {
      const atividadeJaConfigurada = gatilhos.some(
        (g) => g.idTipoAtividade === formGatilho.idTipoAtividade
      );
      if (atividadeJaConfigurada) {
        openNotification({
          isOpen: true,
          message: "Esta atividade já está configurada para as referências selecionadas",
          type: "warning",
        });
        return;
      }
    }

    if (!idEmpresa || !formGatilho.idTipoAtividade) {
      openNotification({
        isOpen: true,
        message: "Selecione um tipo de atividade",
        type: "warning",
      });
      return;
    }

    // T-3.1 — Validação: mínimo 1 sub-tarefa ao salvar
    if (formGatilho.subtarefas.length === 0) {
      openNotification({
        isOpen: true,
        message: "Defina pelo menos uma sub-tarefa para esta atividade",
        type: "error",
      });
      return;
    }

    // T-3.2 — Validação: nome da sub-tarefa mín 3, máx 100 chars
    const nomeInvalido = formGatilho.subtarefas.some(
      (s) => !s.nome || s.nome.trim().length < 3 || s.nome.trim().length > 100
    );
    if (nomeInvalido) {
      openNotification({
        isOpen: true,
        message: "Preencha todos os nomes de sub-tarefa (mínimo 3, máximo 100 caracteres)",
        type: "error",
      });
      return;
    }

    // T-3.3 — Validação: nomes duplicados case-insensitive
    const nomes = formGatilho.subtarefas.map((s) => s.nome.trim().toLowerCase());
    const temDuplicado = nomes.some((n, i) => nomes.indexOf(n) !== i);
    if (temDuplicado) {
      openNotification({
        isOpen: true,
        message: "Já existe uma sub-tarefa com este nome. Use um nome diferente.",
        type: "error",
      });
      return;
    }

    setIsSaving(true);
    try {
      const request: ISalvarGatilhoRequest = {
        idEmpresaTarefaGatilho: gatilhoEditando?.idEmpresaTarefaGatilho,
        idEmpresa,
        idTipoAtividade: formGatilho.idTipoAtividade,
        ativo: formGatilho.ativo,
        subtarefas: formGatilho.subtarefas,
      };

      await salvarGatilho(request);
      openNotification({
        isOpen: true,
        message: "Configuração de gatilho salva com sucesso!",
        type: "success",
      });
      handleFecharModalGatilho();
      await carregarDadosIniciais();
    } catch (error) {
      openNotification({
        isOpen: true,
        message: "Erro ao salvar gatilho",
        type: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleAlterarStatusGatilho = async (gatilho: IEmpresaTarefaGatilho) => {
    try {
      await alterarStatusGatilho({
        idEmpresaTarefaGatilho: gatilho.idEmpresaTarefaGatilho,
        ativo: !gatilho.ativo,
      });
      openNotification({
        isOpen: true,
        message: `Gatilho ${gatilho.ativo ? "desativado" : "ativado"} com sucesso!`,
        type: "success",
      });
      await carregarDadosIniciais();
    } catch (error) {
      openNotification({
        isOpen: true,
        message: "Erro ao alterar status do gatilho",
        type: "error",
      });
    }
  };

  const handleConfirmarExcluirGatilho = async () => {
    if (!gatilhoExcluir) return;

    try {
      await excluirGatilho(gatilhoExcluir);
      openNotification({
        isOpen: true,
        message: "Gatilho excluído com sucesso!",
        type: "success",
      });
      setModalExcluirOpen(false);
      setGatilhoExcluir(null);
      await carregarDadosIniciais();
    } catch (error) {
      openNotification({
        isOpen: true,
        message: "Erro ao excluir gatilho",
        type: "error",
      });
    }
  };

  // ============================================
  // HANDLERS DE SUB-TAREFAS
  // ============================================

  const handleAdicionarSubtarefa = () => {
    const novaOrdem = formGatilho.subtarefas.length + 1;
    setFormGatilho((prev) => ({
      ...prev,
      subtarefas: [
        ...prev.subtarefas,
        {
          nome: "",

          ordem: novaOrdem,
          ativo: true,
        },
      ],
    }));
    openNotification({ isOpen: true, message: "Sub-tarefa adicionada", type: "success" });
  };

  const handleAtualizarSubtarefa = (
    index: number,
    campo: keyof ISalvarSubtarefaRequest,
    valor: any
  ) => {
    setFormGatilho((prev) => ({
      ...prev,
      subtarefas: prev.subtarefas.map((s, i) =>
        i === index ? { ...s, [campo]: valor } : s
      ),
    }));
  };

  const handleRemoverSubtarefa = async (index: number) => {
    const subtarefa = formGatilho.subtarefas[index];
    if (subtarefa.idEmpresaGatilhoSubtarefa) {
      try {
        await excluirSubtarefa(subtarefa.idEmpresaGatilhoSubtarefa);
      } catch (error: any) {
        openNotification({
          isOpen: true,
          message:
            error?.response?.data?.errors?.Mensagens?.[0] ||
            "Não é possível excluir esta sub-tarefa pois ela já foi utilizada em tarefas.",
          type: "warning",
        });
        return;
      }
    }
    setFormGatilho((prev) => ({
      ...prev,
      subtarefas: prev.subtarefas
        .filter((_, i) => i !== index)
        .map((s, i) => ({ ...s, ordem: i + 1 })),
    }));
    openNotification({ isOpen: true, message: "Sub-tarefa removida com sucesso", type: "success" });
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setFormGatilho((prev) => {
        const oldIndex = prev.subtarefas.findIndex(
          (_, i) => `subtarefa-${i}` === active.id
        );
        const newIndex = prev.subtarefas.findIndex(
          (_, i) => `subtarefa-${i}` === over.id
        );

        const newSubtarefas = arrayMove(prev.subtarefas, oldIndex, newIndex).map(
          (s, i) => ({ ...s, ordem: i + 1 })
        );

        return {
          ...prev,
          subtarefas: newSubtarefas,
        };
      });
      openNotification({ isOpen: true, message: "Ordem das sub-tarefas atualizada", type: "success" });
    }
  };

  // ============================================
  // RENDER
  // ============================================

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  // Handler para salvar tudo (referências + gatilhos)
  const handleSalvarTudo = async () => {
    if (!idEmpresa) return;

    setIsSaving(true);
    try {
      await salvarReferenciasEmpresa({
        idEmpresa,
        idReferencias: referenciaSelecionadas,
      });
      openNotification({
        isOpen: true,
        message: "Configurações de tarefas salvas com sucesso!",
        type: "success",
      });
    } catch (error) {
      openNotification({
        isOpen: true,
        message: "Erro ao salvar configurações",
        type: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Sub-tarefas paginadas
  const subtarefasPaginadas = formGatilho.subtarefas.slice(
    subtarefaPage * subtarefaRowsPerPage,
    subtarefaPage * subtarefaRowsPerPage + subtarefaRowsPerPage
  );

  return (
    <Grid container spacing={2} p={1.5}>
      {/* ============================================ */}
      {/* SEÇÃO: REFERÊNCIAS */}
      {/* ============================================ */}
      <Grid item xs={12}>
        <Box
          sx={{
            border: "2px solid #3C90AD",
            borderRadius: 1,
            p: 2,
            width: "100%",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <Typography
              variant="subtitle1"
              sx={{
                color: "#3C90AD",
                fontWeight: 500,
              }}
            >
              Selecionar referências para a edição ou consulta de tarefas
            </Typography>
            <InfoTooltip text="A empresa em edição apenas poderá visualizar e/ou criar/editar as tarefas relacionadas às referências informadas neste campo." />
            <InfoTooltip text="Esse campo terá relacionamento com o Cadastro de Referências do MOF." />
          </Box>

          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, minHeight: 32 }}>
            {referenciaSelecionadas.length === 0 ? (
              <Typography color="textSecondary" variant="body2">
                Nenhuma referência selecionada
              </Typography>
            ) : (
              referenciasOptions
                .filter((r) => referenciaSelecionadas.includes(r.value))
                .map((ref) => (
                  <Chip
                    key={ref.value}
                    label={ref.label}
                    onDelete={() => {
                      setReferenciasSelecionadas((prev) =>
                        prev.filter((id) => id !== ref.value)
                      );
                    }}
                    sx={{
                      backgroundColor: "#3C90AD",
                      color: "white",
                      "& .MuiChip-deleteIcon": {
                        color: "white",
                      },
                    }}
                  />
                ))
            )}
          </Box>

          <Autocomplete
            multiple
            options={referenciasOptions}
            getOptionLabel={(option) => option.label}
            value={referenciasOptions.filter((r) =>
              referenciaSelecionadas.includes(r.value)
            )}
            onChange={(_, newValue) => {
              setReferenciasSelecionadas(newValue.map((v) => v.value));
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Adicionar referência..."
                size="small"
                sx={{ mt: 2 }}
              />
            )}
            renderTags={() => null}
            sx={{ mt: 1 }}
          />
        </Box>
      </Grid>

      {/* ============================================ */}
      {/* SEÇÃO: GATILHOS PARA TAREFAS DE ROTINA */}
      {/* ============================================ */}
      <Grid item xs={12}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography variant="subtitle1" fontWeight="medium">
              Gatilhos para tarefas de rotina
            </Typography>
            <InfoTooltip text="Cadastro de gatilhos para atividades previamente cadastradas no MOF. Em Gerência > cadastro de Atividades, para utilização nas tarefas" />
          </Box>
          {temPermissaoGerenciarTarefas && (
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => handleAbrirModalGatilho()}
                disabled={referenciaSelecionadas.length === 0}
              >
                ADICIONAR ATIVIDADES
              </Button>
              <InfoTooltip text="Permite que sejam incluídas novas atividades para configuração de gatilho." />
            </Box>
          )}
        </Box>

        {referenciaSelecionadas.length === 0 && (
          <Typography color="textSecondary" align="center" py={2}>
            Selecione referências para visualizar ou configurar tarefas
          </Typography>
        )}

        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
              <TableRow>
                <TableCell>Tipo de Atividade</TableCell>
                <TableCell align="center" width={120}>
                  Ações
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {gatilhos.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={2} align="center">
                    Nenhuma atividade configurada. Clique em 'ADICIONAR ATIVIDADES' para começar.
                  </TableCell>
                </TableRow>
              ) : (
                gatilhos.map((gatilho) => (
                  <TableRow
                    key={gatilho.idEmpresaTarefaGatilho}
                    sx={{ "&:hover": { backgroundColor: "#F9F9F9" }, cursor: "pointer" }}
                  >
                    <TableCell>
                      <Typography variant="body2">
                        {gatilho.nomeTipoAtividade}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      {/* Lupa azul — abre modo visualização (readonly) */}
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleAbrirModalGatilho(gatilho, true)}
                        title="Visualizar"
                        sx={{
                          "&:hover": { backgroundColor: "#F0F0F0" },
                          "&:active": { backgroundColor: "#E0E0E0" },
                        }}
                      >
                        <Search />
                      </IconButton>
                      {/* Lápis azul — abre modo edição */}
                      {temPermissaoGerenciarTarefas && (
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleAbrirModalGatilho(gatilho, false)}
                          title="Editar"
                          sx={{
                            "&:hover": { backgroundColor: "#F0F0F0" },
                            "&:active": { backgroundColor: "#E0E0E0" },
                          }}
                        >
                          <Edit />
                        </IconButton>
                      )}
                      {/* Lixeira vermelha — abre confirmação de exclusão */}
                      {temPermissaoGerenciarTarefas && (
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => {
                            setGatilhoExcluir(gatilho.idEmpresaTarefaGatilho);
                            setModalExcluirOpen(true);
                          }}
                          title="Excluir"
                          sx={{
                            "&:hover": { backgroundColor: "#F0F0F0" },
                            "&:active": { backgroundColor: "#E0E0E0" },
                          }}
                        >
                          <Delete />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>

      {/* ============================================ */}
      {/* RODAPÉ: BOTÕES CANCELAR E SALVAR */}
      {/* ============================================ */}
      <Grid item xs={12}>
        <Divider sx={{ my: 3 }} />
        <Box display="flex" justifyContent="flex-end" gap={2}>
          <Button variant="outlined" onClick={onCancel}>
            CANCELAR
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSalvarTudo}
            disabled={isSaving}
          >
            {isSaving ? <CircularProgress size={24} /> : "SALVAR"}
          </Button>
        </Box>
      </Grid>

      {/* ============================================ */}
      {/* MODAL: EDIÇÃO / VISUALIZAÇÃO DE GATILHO */}
      {/* ============================================ */}
      <Dialog
        open={modalGatilhoOpen}
        onClose={() => {
          const houveMudanca = JSON.stringify(formGatilho) !== formGatilhoOriginal;
          if (houveMudanca && !modoVisualizacao) {
            setModalDescartarOpen(true);
          } else {
            handleFecharModalGatilho();
          }
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Configuração de gatilho da atividade:
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={8}>
              {gatilhoEditando ? (
                <TextField
                  fullWidth
                  label="Tipo de Atividade"
                  value={
                    tiposAtividade.find(
                      (t) => t.idTipoAtividade === formGatilho.idTipoAtividade
                    )?.nome || ""
                  }
                  disabled
                  sx={{ backgroundColor: "#F5F5F5" }}
                />
              ) : (
                <Autocomplete
                  options={tiposAtividade}
                  getOptionLabel={(option) => option.nome}
                  value={
                    tiposAtividade.find(
                      (t) => t.idTipoAtividade === formGatilho.idTipoAtividade
                    ) || null
                  }
                  onChange={(_, newValue) => {
                    setFormGatilho((prev) => ({
                      ...prev,
                      idTipoAtividade: newValue?.idTipoAtividade || null,
                    }));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Tipo de Atividade *"
                      placeholder="Selecione o tipo"
                    />
                  )}
                />
              )}
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formGatilho.ativo}
                    onChange={(e) =>
                      setFormGatilho((prev) => ({
                        ...prev,
                        ativo: e.target.checked,
                      }))
                    }
                    disabled={modoVisualizacao}
                  />
                }
                label="Ativo"
              />
            </Grid>

            {/* Sub-tarefas */}
            <Grid item xs={12}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
              >
                <Typography variant="subtitle1" fontWeight="medium">
                  Defina as sub-tarefas para essa atividade:
                </Typography>
                {!modoVisualizacao && (
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      onClick={handleAdicionarSubtarefa}
                    >
                      ADICIONAR SUB-TAREFA
                    </Button>
                    <InfoTooltip text="Ao clicar nesse botão uma nova linha é aberta para o cadastro de uma nova sub-tarefa." />
                  </Box>
                )}
              </Box>

              {formGatilho.subtarefas.length === 0 ? (
                <Typography color="textSecondary" align="center" py={2}>
                  Nenhuma sub-tarefa definida. Clique em 'ADICIONAR SUB-TAREFA' para começar.
                </Typography>
              ) : (
                <>
                  <DndContext
                    sensors={modoVisualizacao ? [] : sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext
                      items={formGatilho.subtarefas.map((_, i) => `subtarefa-${i}`)}
                      strategy={verticalListSortingStrategy}
                    >
                      <TableContainer
                        component={Paper}
                        variant="outlined"
                        sx={{ maxHeight: 300, overflow: "auto" }}
                      >
                        <Table size="small" stickyHeader>
                          <TableHead>
                            <TableRow>
                              <TableCell width={40}></TableCell>
                              <TableCell width={40}>#</TableCell>
                              <TableCell>Sub-tarefa</TableCell>
                              <TableCell width={60} align="center"></TableCell>
                              {!modoVisualizacao && <TableCell width={50}></TableCell>}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {subtarefasPaginadas.map((subtarefa, pageIndex) => {
                              const realIndex = subtarefaPage * subtarefaRowsPerPage + pageIndex;
                              return (
                                <SortableSubtarefaRow
                                  key={`subtarefa-${realIndex}`}
                                  id={`subtarefa-${realIndex}`}
                                  index={realIndex}
                                  subtarefa={subtarefa}
                                  modoVisualizacao={modoVisualizacao}
                                  onUpdate={handleAtualizarSubtarefa}
                                  onRemove={handleRemoverSubtarefa}
                                  onRequestDelete={(idx) => {
                                    setSubtarefaExcluirIndex(idx);
                                    setModalExcluirSubtarefaOpen(true);
                                  }}
                                />
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </SortableContext>
                  </DndContext>

                  {formGatilho.subtarefas.length > 10 && (
                    <TablePagination
                      component="div"
                      count={formGatilho.subtarefas.length}
                      page={subtarefaPage}
                      onPageChange={(_, newPage) => setSubtarefaPage(newPage)}
                      rowsPerPage={subtarefaRowsPerPage}
                      onRowsPerPageChange={(e) => {
                        setSubtarefaRowsPerPage(parseInt(e.target.value, 10));
                        setSubtarefaPage(0);
                      }}
                      rowsPerPageOptions={[10, 25, 50, 100]}
                      labelRowsPerPage="Linhas por página:"
                    />
                  )}
                </>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              const houveMudanca = JSON.stringify(formGatilho) !== formGatilhoOriginal;
              if (houveMudanca && !modoVisualizacao) {
                setModalDescartarOpen(true);
              } else {
                handleFecharModalGatilho();
              }
            }}
          >
            {modoVisualizacao ? "Fechar" : "Cancelar"}
          </Button>
          {!modoVisualizacao && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleSalvarGatilho}
              disabled={isSaving}
            >
              {isSaving ? <CircularProgress size={24} /> : "Salvar"}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* ============================================ */}
      {/* MODAL: CONFIRMAÇÃO DE EXCLUSÃO DE GATILHO */}
      {/* ============================================ */}
      <ConfirmDeleteModal
        title="Excluir Gatilho"
        description="Tem certeza que deseja excluir este gatilho? Todas as sub-tarefas associadas também serão excluídas."
        open={modalExcluirOpen}
        onClose={() => {
          setModalExcluirOpen(false);
          setGatilhoExcluir(null);
        }}
        onConfirm={handleConfirmarExcluirGatilho}
      />

      {/* ============================================ */}
      {/* MODAL: CONFIRMAÇÃO DE DESCARTE DE ALTERAÇÕES */}
      {/* ============================================ */}
      <Dialog open={modalDescartarOpen} onClose={() => setModalDescartarOpen(false)}>
        <DialogTitle>Descartar Alterações</DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja sair sem salvar? As alterações serão perdidas.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalDescartarOpen(false)}>Voltar</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => {
              setModalDescartarOpen(false);
              handleFecharModalGatilho();
            }}
          >
            Sim, descartar
          </Button>
        </DialogActions>
      </Dialog>

      {/* ============================================ */}
      {/* MODAL: CONFIRMAÇÃO DE EXCLUSÃO DE SUB-TAREFA */}
      {/* ============================================ */}
      <Dialog
        open={modalExcluirSubtarefaOpen}
        onClose={() => setModalExcluirSubtarefaOpen(false)}
      >
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <Typography>Tem certeza que deseja excluir esta sub-tarefa?</Typography>
          {subtarefaExcluirIndex !== null && (
            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
              Sub-tarefa: {formGatilho.subtarefas[subtarefaExcluirIndex]?.nome || "(sem nome)"}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalExcluirSubtarefaOpen(false)}>Cancelar</Button>
          <Button
            color="error"
            variant="contained"
            onClick={async () => {
              if (subtarefaExcluirIndex !== null) {
                await handleRemoverSubtarefa(subtarefaExcluirIndex);
              }
              setModalExcluirSubtarefaOpen(false);
              setSubtarefaExcluirIndex(null);
            }}
          >
            Excluir
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};
